import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { procurement, ApiError } from "../lib/api";
import { money, uuid } from "../lib/utils";
import { useActiveBranch } from "../lib/session";

// Compras: lista de OCs + RECEPCIÓN DURA. La recepción permite por línea:
// cantidad buena (entra a stock), avería (no entra), costo real; + N° de factura
// del proveedor. Todo vía procurement_receive (idempotente por client_uuid).
export const Route = createFileRoute("/compras")({ component: Compras });

interface PoRow { id: string; status: string; total: number; expected_at: string | null; supplier_id: string }
interface PoLine { id: string; product_id: string; qty: number; qty_received: number; unit_cost: number; product: { sku: string; name: string } }

function Compras() {
  const [receivePo, setReceivePo] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { data: pos, isLoading } = useQuery({
    queryKey: ["purchase_orders"],
    queryFn: async () => {
      const { data, error } = await supabase.from("purchase_orders")
        .select("id, status, total, expected_at, supplier_id").in("status", ["sent", "partial"]).order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PoRow[];
    },
  });

  if (receivePo) return <ReceiveScreen poId={receivePo} onDone={() => setReceivePo(null)} />;
  if (creating) return <NewPurchaseOrder onDone={() => setCreating(false)} onCancel={() => setCreating(false)} />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Compras — Órdenes por recibir</h1>
        <button onClick={() => setCreating(true)} className="btn btn-primary">+ Nueva orden</button>
      </div>
      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr><th className="px-4 py-2">OC</th><th className="px-4 py-2">Estado</th><th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2">Esperada</th><th className="px-4 py-2" /></tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-grafito-400">Cargando…</td></tr>
              : (pos ?? []).length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-grafito-400">No hay órdenes por recibir.</td></tr>
              : pos!.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="px-4 py-2 font-mono text-xs">{p.id.slice(0, 8)}</td>
                  <td className="px-4 py-2"><span className="rounded bg-grafito-100 px-2 py-0.5 text-xs">{p.status}</span></td>
                  <td className="px-4 py-2 text-right">{money(p.total)}</td>
                  <td className="px-4 py-2">{p.expected_at ?? "—"}</td>
                  <td className="px-4 py-2 text-right"><button onClick={() => setReceivePo(p.id)} className="btn btn-primary py-1 text-xs">Recibir</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface RLine { po_line_id: string; product_id: string; sku: string; name: string; ordered: number; received: number; good: string; damaged: string; cost: string }

function ReceiveScreen({ poId, onDone }: { poId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [invoiceNo, setInvoiceNo] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const { data: lines, isLoading } = useQuery({
    queryKey: ["po_lines", poId],
    queryFn: async () => {
      const { data, error } = await supabase.from("purchase_order_lines")
        .select("id, product_id, qty, qty_received, unit_cost, product:products(sku, name)").eq("po_id", poId);
      if (error) throw error;
      return (data as unknown as PoLine[]).map<RLine>((l) => ({
        po_line_id: l.id, product_id: l.product_id, sku: l.product.sku, name: l.product.name,
        ordered: l.qty, received: l.qty_received,
        good: String(l.qty - l.qty_received), damaged: "0", cost: String(l.unit_cost),
      }));
    },
  });

  const [rows, setRows] = useState<RLine[] | null>(null);
  const data = rows ?? lines ?? [];
  function setRow(i: number, patch: Partial<RLine>) {
    setRows((cur) => { const base = cur ?? lines ?? []; const cp = [...base]; cp[i] = { ...cp[i], ...patch }; return cp; });
  }

  const receive = useMutation({
    mutationFn: () => procurement.receive(poId,
      data.filter((r) => parseFloat(r.good) > 0 || parseFloat(r.damaged) > 0).map((r) => ({
        po_line_id: r.po_line_id, product_id: r.product_id,
        qty_base: parseFloat(r.good) || 0, qty_damaged: parseFloat(r.damaged) || 0, unit_cost: parseFloat(r.cost) || 0,
      })),
      uuid(),
      { invoiceNo: invoiceNo || undefined, invoiceDate: new Date().toISOString().slice(0, 10) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["purchase_orders"] }); onDone(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  if (isLoading) return <div className="text-grafito-400">Cargando líneas…</div>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Recepción de mercadería</h1>
        <button onClick={onDone} className="btn btn-ghost">← Volver</button>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border bg-white p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-grafito-400">N° factura del proveedor</label>
          <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="Ej: PROV-001"
            className="rounded-lg border px-3 py-2 outline-none focus:border-brand" />
        </div>
        <p className="text-xs text-grafito-400">El costo promedio se recalcula con lo bueno recibido. La avería no entra a stock.</p>
      </div>

      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr>
              <th className="px-3 py-2">Producto</th><th className="px-3 py-2 text-right">Pedido</th>
              <th className="px-3 py-2 text-right">Ya recibido</th><th className="px-3 py-2 text-right">Bueno</th>
              <th className="px-3 py-2 text-right">Avería</th><th className="px-3 py-2 text-right">Costo unit.</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => (
              <tr key={r.po_line_id} className="border-t">
                <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="font-mono text-xs text-grafito-400">{r.sku}</div></td>
                <td className="px-3 py-2 text-right">{r.ordered}</td>
                <td className="px-3 py-2 text-right text-grafito-400">{r.received}</td>
                <td className="px-3 py-2"><input type="number" min="0" value={r.good} onChange={(e) => setRow(i, { good: e.target.value })} className="w-20 rounded border px-2 py-1 text-right" /></td>
                <td className="px-3 py-2"><input type="number" min="0" value={r.damaged} onChange={(e) => setRow(i, { damaged: e.target.value })} className="w-20 rounded border px-2 py-1 text-right" /></td>
                <td className="px-3 py-2"><input type="number" min="0" step="0.01" value={r.cost} onChange={(e) => setRow(i, { cost: e.target.value })} className="w-24 rounded border px-2 py-1 text-right" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {msg && <div className="mt-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{msg}</div>}
      <div className="mt-4 flex justify-end">
        <button onClick={() => receive.mutate()} disabled={receive.isPending} className="btn btn-primary px-6 py-2.5 disabled:opacity-40">
          {receive.isPending ? "Recibiendo…" : "✓ Confirmar recepción"}
        </button>
      </div>
    </div>
  );
}

// ---------- Nueva orden de compra ----------
interface DraftLine { sku: string; name?: string; productId?: string; productUnitId?: string; qty: string; cost: string }

function NewPurchaseOrder({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const branch = useActiveBranch();
  const qc = useQueryClient();
  const [supplierId, setSupplierId] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([{ sku: "", qty: "", cost: "" }]);
  const [msg, setMsg] = useState<string | null>(null);
  const [newSupplier, setNewSupplier] = useState(false);

  const suppliers = useQuery({
    queryKey: ["suppliers"],
    queryFn: async () => {
      const { data } = await supabase.from("suppliers").select("id, name").eq("is_active", true).order("name");
      return (data ?? []) as { id: string; name: string }[];
    },
  });

  function setLine(i: number, patch: Partial<DraftLine>) { setLines((c) => { const cp = [...c]; cp[i] = { ...cp[i], ...patch }; return cp; }); }

  // Resuelve SKU → producto + presentación base (factor 1, la de compra por defecto).
  async function resolveLine(i: number) {
    const sku = lines[i].sku.trim(); if (!sku) return;
    const { data: prod } = await supabase.from("products").select("id, name").eq("sku", sku).maybeSingle();
    if (!prod) { setLine(i, { name: "—", productId: undefined, productUnitId: undefined }); return; }
    const p = prod as { id: string; name: string };
    const { data: pu } = await supabase.from("product_units")
      .select("id").eq("product_id", p.id).eq("factor", 1).is("valid_to", null).maybeSingle();
    setLine(i, { name: p.name, productId: p.id, productUnitId: (pu as { id: string } | null)?.id });
  }

  // Recuerda la OC ya creada: si poCreate tuvo éxito pero poSend falló, al
  // reintentar solo se reenvía (idempotente) en vez de crear una OC duplicada (bug 4).
  const createdPoId = useRef<string | null>(null);
  const create = useMutation({
    mutationFn: async () => {
      const valid = lines.filter((l) => l.productId && l.productUnitId && parseFloat(l.qty) > 0);
      if (valid.length === 0) throw new Error("Agrega al menos una línea válida (SKU resuelto, cantidad > 0).");
      let poId = createdPoId.current;
      if (!poId) {
        const po = await procurement.poCreate(supplierId, branch,
          valid.map((l) => ({ product_id: l.productId!, product_unit_id: l.productUnitId!, qty: parseFloat(l.qty), unit_cost: parseFloat(l.cost) || 0 })));
        poId = (po as { id: string }).id;
        createdPoId.current = poId;   // si poSend falla, el reintento no recrea
      }
      await procurement.poSend(poId);
      return poId;
    },
    onSuccess: () => { createdPoId.current = null; void qc.invalidateQueries({ queryKey: ["purchase_orders"] }); onDone(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Nueva orden de compra</h1>
        <button onClick={onCancel} className="btn btn-ghost">← Volver</button>
      </div>
      {msg && <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{msg}</div>}

      <div className="mb-4 rounded-xl border bg-white p-4">
        <label className="mb-1 block text-sm font-medium">Proveedor</label>
        {!newSupplier ? (
          <div className="flex items-center gap-3">
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="rounded-lg border px-3 py-2">
              <option value="">— elegir —</option>
              {(suppliers.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={() => setNewSupplier(true)} className="text-sm text-brand">+ nuevo proveedor</button>
          </div>
        ) : <NewSupplierInline onCreated={(id) => { setSupplierId(id); setNewSupplier(false); void suppliers.refetch(); }} onCancel={() => setNewSupplier(false)} />}
      </div>

      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr><th className="px-3 py-2">SKU</th><th className="px-3 py-2">Producto</th><th className="px-3 py-2 text-right">Cantidad</th><th className="px-3 py-2 text-right">Costo unit.</th><th /></tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2"><input value={l.sku} onChange={(e) => setLine(i, { sku: e.target.value })} onBlur={() => resolveLine(i)} placeholder="SKU" className="w-32 rounded border px-2 py-1" /></td>
                <td className="px-3 py-2 text-xs text-grafito-400">{l.name ?? ""}</td>
                <td className="px-3 py-2"><input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="w-24 rounded border px-2 py-1 text-right" /></td>
                <td className="px-3 py-2"><input type="number" value={l.cost} onChange={(e) => setLine(i, { cost: e.target.value })} className="w-24 rounded border px-2 py-1 text-right" /></td>
                <td className="px-3 py-2 text-right">{lines.length > 1 && <button onClick={() => setLines((c) => c.filter((_, j) => j !== i))} className="text-xs text-grafito-400">✕</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={() => setLines((c) => [...c, { sku: "", qty: "", cost: "" }])} className="m-3 text-sm text-brand">+ agregar línea</button>
      </div>

      <div className="mt-4 flex justify-end">
        <button onClick={() => create.mutate()} disabled={!supplierId || create.isPending} className="btn btn-primary px-6 py-2.5 disabled:opacity-40">
          {create.isPending ? "Creando…" : "Crear y enviar OC"}
        </button>
      </div>
    </div>
  );
}

function NewSupplierInline({ onCreated, onCancel }: { onCreated: (id: string) => void; onCancel: () => void }) {
  const [name, setName] = useState(""); const [terms, setTerms] = useState("30"); const [msg, setMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => procurement.supplierUpsert({ name, paymentTermsDays: parseInt(terms) || 0 }),
    onSuccess: (s) => onCreated((s as { id: string }).id),
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <div className="flex flex-wrap items-center gap-2">
      {msg && <span className="text-xs text-red-600">{msg}</span>}
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre del proveedor" className="rounded-lg border px-3 py-2 text-sm" />
      <input type="number" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Días crédito" className="w-28 rounded-lg border px-3 py-2 text-sm" />
      <button onClick={() => m.mutate()} disabled={!name.trim() || m.isPending} className="btn btn-primary py-1 text-xs">Guardar</button>
      <button onClick={onCancel} className="text-xs text-grafito-400">cancelar</button>
    </div>
  );
}
